"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent } from "react";
import Icon from "./SolIcon";
import { enterprise, industry, intelligence, slugify, tabs, type TabKey } from "./solutionsData";

/* ---------- scroll slide-up reveal (scoped to .sol-page) ---------- */
export function SolReveal() {
  useEffect(() => {
    const root = document.querySelector(".sol-page");
    if (!root) return;
    root.classList.add("sol-js");
    const SEL = ".sol-reveal, .sol-stagger, .sol-card";
    const show = (el: Element) => el.classList.add("is-visible");

    if (!("IntersectionObserver" in window)) {
      root.querySelectorAll(SEL).forEach(show);
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            show(e.target);
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    root.querySelectorAll(SEL).forEach((el) => io.observe(el));

    // cards re-mount whenever the tab changes: observe those as well
    const mo = new MutationObserver((muts) =>
      muts.forEach((m) =>
        m.addedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          if (n.matches(SEL)) io.observe(n);
          n.querySelectorAll(SEL).forEach((el) => io.observe(el));
        })
      )
    );
    mo.observe(root, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); };
  }, []);
  return null;
}

const Arrow = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

const delay = (i: number, per = 0.06, cap = 8) =>
  ({ "--d": `${(i % cap) * per}s` } as CSSProperties);

export default function SolutionsTabs() {
  const [active, setActive] = useState<TabKey>("intelligence");
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  // deep-link support: /solutions#enterprise
  useEffect(() => {
    const h = window.location.hash.replace("#", "") as TabKey;
    if (tabs.some((t) => t.key === h)) setActive(h);
  }, []);

  const select = useCallback((k: TabKey) => {
    setActive(k);
    window.history.replaceState(null, "", `#${k}`);
  }, []);

  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    let n = i;
    if (e.key === "ArrowRight") n = (i + 1) % tabs.length;
    else if (e.key === "ArrowLeft") n = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") n = 0;
    else if (e.key === "End") n = tabs.length - 1;
    else return;
    e.preventDefault();
    select(tabs[n].key);
    refs.current[tabs[n].key]?.focus();
  };

  const current = tabs.find((t) => t.key === active)!;
  const count = active === "intelligence" ? intelligence.length : active === "enterprise" ? enterprise.length : industry.length;

  return (
    <section className="sol-wrap sol-tabs-section" id="solutions">
      {/* TAB HEADERS */}
      <div className="sol-tablist sol-reveal" role="tablist" aria-label="Solution categories">
        {tabs.map((t, i) => (
          <button
            key={t.key}
            ref={(el) => { refs.current[t.key] = el; }}
            id={`tab-${t.key}`}
            role="tab"
            type="button"
            aria-selected={active === t.key}
            aria-controls={`panel-${t.key}`}
            tabIndex={active === t.key ? 0 : -1}
            className={`sol-tab${active === t.key ? " is-active" : ""}`}
            onClick={() => select(t.key)}
            onKeyDown={(e) => onKey(e, i)}
          >
            <span className="sol-tab-icon"><Icon name={t.icon} size={38} /></span>
            <span className="sol-tab-text">
              <strong>{t.title}</strong>
              <small>{t.tagline}</small>
            </span>
          </button>
        ))}
      </div>

      {/* PANEL */}
      <div
        className="sol-panel"
        role="tabpanel"
        id={`panel-${active}`}
        aria-labelledby={`tab-${active}`}
        key={active}
      >
        <div className="sol-panel-head">
          <p>{current.intro}</p>
          <span className="sol-count">{count} solutions</span>
        </div>

        {active === "intelligence" && (
          <div className="sol-grid sol-grid-4">
            {intelligence.map((s, i) => (
              <Link key={s.no} href={`/solutions/${slugify(s.title)}`} className="sol-card sol-int" style={delay(i)}>
                <span className="sol-badge">{s.no}</span>
                <span className="sol-int-icon"><Icon name={s.icon} size={38} /></span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
                <span className="sol-go"><Arrow /></span>
              </Link>
            ))}
          </div>
        )}

        {active === "enterprise" && (
          <div className="sol-grid sol-grid-3">
            {enterprise.map((s, i) => (
              <Link key={s.title} href={`/solutions/${slugify(s.title)}`} className="sol-card sol-ent" style={delay(i)}>
                <div className="sol-ent-top">
                  <span className="sol-ent-icon"><Icon name={s.icon} size={34} /></span>
                  <h3>{s.title}</h3>
                </div>
                <p>{s.text}</p>
                <span className="sol-go"><Arrow /></span>
              </Link>
            ))}
          </div>
        )}

        {active === "industry" && (
          <div className="sol-grid sol-grid-2">
            {industry.map((s, i) => (
              <Link key={s.title} href={s.href} className="sol-card sol-ind" style={delay(i)}>
                <span className="sol-ind-img" style={{ backgroundImage: `url('${s.image}')` }} />
                <span className="sol-ind-body">
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </span>
                <span className="sol-ind-arrow"><Arrow size={16} /></span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
